import {useDataTracking} from '#imports';

type PurchaseOrderSource =
    | 'quote_acceptance_page'
    | 'client_project_list'
    | 'client_project_view'
    | 'timesheet_approval_page'
    | 'expense_approval_page'
    | 'task_based_project_approval_page';

type PurchaseOrderChange = 'added' | 'updated' | 'deleted' | 'unchanged';

export const useTrackPurchaseOrder = () => {
    const {track} = useDataTracking();

    function resolvePurchaseOrderChange(previousPurchaseOrder?: string, submittedPurchaseOrder?: string): PurchaseOrderChange {
        const previous = previousPurchaseOrder?.trim() ?? '';
        const submitted = submittedPurchaseOrder?.trim() ?? '';

        if (previous === submitted) return 'unchanged';
        if (!previous) return 'added';
        if (!submitted) return 'deleted';
        return 'updated';
    }

    async function trackPurchaseOrderAdded(source: PurchaseOrderSource, hasMandatoryPurchaseOrder: boolean) {
        return await track('purchase_order_added', {
            source,
            has_mandatory_purchase_order: hasMandatoryPurchaseOrder,
        });
    }

    async function trackPurchaseOrderUpdated(source: PurchaseOrderSource, hasMandatoryPurchaseOrder: boolean) {
        return await track('purchase_order_updated', {
            source,
            has_mandatory_purchase_order: hasMandatoryPurchaseOrder,
        });
    }

    async function trackPurchaseOrderDeleted(source: 'client_project_view' | 'timesheet_approval_page' | 'task_based_project_approval_page') {
        return await track('purchase_order_deleted', {
            source,
        });
    }

    async function trackPurchaseOrderAddedOrUpdated(
        purchaseOrderChange: PurchaseOrderChange,
        source: PurchaseOrderSource,
        hasMandatoryPurchaseOrder: boolean,
    ) {
        if (purchaseOrderChange === 'added') return await trackPurchaseOrderAdded(source, hasMandatoryPurchaseOrder);
        if (purchaseOrderChange === 'updated') return await trackPurchaseOrderUpdated(source, hasMandatoryPurchaseOrder);
    }

    return {
        resolvePurchaseOrderChange,
        trackPurchaseOrderAdded,
        trackPurchaseOrderUpdated,
        trackPurchaseOrderDeleted,
        trackPurchaseOrderAddedOrUpdated,
    };
};
